import { SearchFiltersMobileProps } from '../SearchFilters/SearchFiltersMobile/SearchFiltersMobile.component';
import Head from 'next/head';
import { SearchContext } from 'pages/recherche';
import { ActivityCode, InterestCode } from 'pleinchamp-api-client';
import type { ReactNode } from 'react';
import React, { FC, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PrimaryButton } from '@components/buttons/FlatButton/FlatButton.component';
import { PlcMessageWithRetryProps } from '@components/PlcMessage/PlcMessageWithRetry.component';
import { Text } from '@components/Text/Text.component';
import { ContentCardLoaderJournalVersion } from '@content/ContentCardJournalVersion/ContentCardLoaderJournalVersion.component';
import { PlcAnchor } from '@layout/components/PlcAnchor/PlcAnchor.component';
import { Filter, FilterType, SearchContentType } from '@search/business/search.types';
import { generateDefaultFilters } from '@search/business/search.utils';
import { Breakpoint, useClassNames, useIsBreakpointDown } from '@styles/breakpoints';
import { buildContentCard } from '@utils/card';
import { usePlcRouter } from '@utils/customHooks';
import { defaultExtendedPagination } from '@utils/pagination';
import { formatUrlWithParam, urlEnum } from '@utils/url';
import classnames from 'classnames';
import dynamic from 'next/dynamic';
import styles from './SearchResults.module.scss';

const PlcMessageWithRetry = dynamic<PlcMessageWithRetryProps>(
  () => import('@components/PlcMessage/PlcMessageWithRetry.component').then((mod) => mod.PlcMessageWithRetry),
  {}
);

const SearchNoResult = dynamic<{}>(
  () => import('../SearchNoResult/SearchNoResult.component').then((mod) => mod.SearchNoResult),
  {}
);

const SearchFiltersMobile = dynamic<SearchFiltersMobileProps>(
  () =>
    import('../SearchFilters/SearchFiltersMobile/SearchFiltersMobile.component').then((mod) => mod.SearchFiltersMobile),
  {}
);

const SearchResults: FC = () => {
  const {
    results: { data: results, isValidating: loadingResults, error: errorResults, retryCallback: retryFetchResults },
    pagination: { data: tempPagination },
    nextResults: { fetchNextResults, isValidating: loadingNext },
  } = useContext(SearchContext);
  const pagination = tempPagination || defaultExtendedPagination;
  const isSmartphone = useIsBreakpointDown(Breakpoint.s);
  const isMobile = useIsBreakpointDown(Breakpoint.m);
  const {
    interests: { data: tempInterests, setData: setInterests },
    activities: { data: tempActivities, setData: setActivities },
  } = useContext(SearchContext);

  const interests = tempInterests || [];
  const activities = tempActivities || [];
  const { t } = useTranslation(['search']);
  const themeFilters = useMemo(
    () =>
      generateDefaultFilters({ t, type: FilterType.Themes }).map((f) => ({
        ...f,
        active: interests.includes(f.value as InterestCode),
      })),
    [interests]
  );

  const activityFilters = useMemo(
    () =>
      generateDefaultFilters({ t, type: FilterType.Activities }).map((f) => ({
        ...f,
        active: activities.includes(f.value as ActivityCode),
      })),
    [activities]
  );
  const cards = results?.map((item) =>
    buildContentCard(
      item,
      isSmartphone,
      'journal-version',
      undefined,
      undefined,
      isMobile ? 210 : 220,
      isMobile ? 90 : 120
    )
  );

  const hasResult = results && results.length > 0;

  const onSeeMoreClick = () => {
    if (fetchNextResults) {
      fetchNextResults();
    }
  };

  const { query: routerQuery, push } = usePlcRouter();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [!fetchNextResults]);

  useEffect(() => {
    const { offset, count } = pagination;
    if (offset > count) {
      push('/404');
    }
  }, [pagination]);

  function buildSeoNextSearchPageLinks() {
    const { offset, limit, count } = pagination;
    const anchors: ReactNode[] = [];
    for (let i = 0; i < count; i += limit) {
      const searchPage = Math.ceil(i / limit).toString();
      if (i === offset) {
        continue;
      }
      anchors.push(
        <PlcAnchor
          key={i}
          linkProps={{
            href: formatUrlWithParam(urlEnum.search, {
              ...routerQuery,
              page: searchPage,
            }),
          }}
        />
      );
    }
    return anchors;
  }
  const loading = loadingResults && !loadingNext;
  const { selectedThemes, selectedActivities, selectedFilters } = useMemo(() => {
    const activeThemes: Filter[] = themeFilters.filter((f) => f.active);
    const activeActivities: Filter[] = activityFilters.filter((f) => f.active);
    const activeFilters: Filter[] = [...activeThemes, ...activeActivities];
    return {
      nbFilters: activeFilters.length,
      selectedActivities: activeActivities,
      selectedFilters: activeFilters,
      selectedThemes: activeThemes,
    };
  }, [activityFilters, themeFilters]);

  const {
    counts: { data: counts },
    contentType: { data: contentType },
  } = useContext(SearchContext);

  function renderContent() {
    if (loading) {
      return (
        <>
          <ContentCardLoaderJournalVersion uniqueKey="searchResults1" />
          <ContentCardLoaderJournalVersion uniqueKey="searchResults2" />
          <ContentCardLoaderJournalVersion uniqueKey="searchResults3" />
        </>
      );
    }
    if (errorResults) {
      return <PlcMessageWithRetry retryCallback={retryFetchResults} />;
    }
    if (hasResult) {
      return cards;
    }
    return <SearchNoResult />;
  }

  const nbResultsClass = useClassNames({ all: 'plc-my-s', m: 'plc-mb-0' });

  let count = 0;
  if (counts) {
    switch (contentType) {
      case SearchContentType.Guide:
        count = counts.guide;
        break;
      case SearchContentType.Event:
        count = counts.event;
        break;
      case SearchContentType.Service:
        count = counts.service;
        break;
      case SearchContentType.Article:
        count = counts.article;
        break;
      default:
        count = counts.all;
    }
  }
  return (
    <main className={classnames('wrapper-s', useIsBreakpointDown(Breakpoint.l) ? 'plc-mt-xxl' : 'plc-mt-m')}>
      <SearchFiltersMobile
        activityFilters={activityFilters}
        selectedActivities={selectedActivities}
        selectedFilters={selectedFilters}
        selectedThemes={selectedThemes}
        setActivities={setActivities}
        setInterests={setInterests}
        t={t}
        themeFilters={themeFilters}
      />
      <Head>
        <link href={formatUrlWithParam(urlEnum.search, routerQuery)} key="canonical" rel="canonical" />
        {Number(routerQuery.page) > 0 && (
          <link
            href={formatUrlWithParam(urlEnum.search, {
              ...routerQuery,
              page: (Number(routerQuery.page) - 1).toString(),
            })}
            key="link-prev"
            rel="prev"
          />
        )}
        {pagination.hasNext && (
          <link
            href={formatUrlWithParam(urlEnum.search, {
              ...routerQuery,
              page: ((Number(routerQuery.page) || 0) + 1).toString(),
            })}
            key="link-next"
            rel="next"
          />
        )}
      </Head>
      {!loading && count > 0 && (
        <Text
          alignment="right"
          className={classnames(nbResultsClass, styles['nb-resultat'])}
          flavour="meteo-dark-green"
          i18nKey="search.filter.all"
          i18nOptions={{
            count,
          }}
          style={{ fontWeight: 'normal' }}
          tag="div"
          variant="h6"
        />
      )}
      <div className={classnames(styles.resultat)}>{renderContent()}</div>
      {!loading && !loadingNext && hasResult && !errorResults && (
        <div className={classnames('plc-mt-m', styles['voir-plus-button'])}>
          {pagination.hasNext && (
            <PrimaryButton
              isLightGreen
              onClick={onSeeMoreClick}
              textProps={{ i18nKey: 'search.journal.results.see-more' }}
            />
          )}
          <div>{buildSeoNextSearchPageLinks()}</div>
        </div>
      )}
      {loadingNext && (
        <>
          <ContentCardLoaderJournalVersion uniqueKey="searchNextResults1" />
          <ContentCardLoaderJournalVersion uniqueKey="searchNextResults2" />
          <ContentCardLoaderJournalVersion uniqueKey="searchNextResults3" />
        </>
      )}
    </main>
  );
};

export { SearchResults };
